import type { HistoryItem } from "../types";
import { STATUS_CONFIG, TYPE_CONFIG } from "../types";

interface Props {
  items: HistoryItem[];
  currentPage: number;
  totalPages: number;
  onPageChange: (p: number) => void;
  onSelect: (item: HistoryItem) => void;
  onCancel: (item: HistoryItem) => void;
  cancellingIds: Set<string>;
  selectedIds: Set<string>;
  onToggleSelect: (id: string) => void;
  onToggleSelectAll: () => void;
  onClearSelection: () => void;
  onBatchDelete: () => void;
}

export default function HistoryTable({ items, currentPage, totalPages, onPageChange, onSelect, onCancel, cancellingIds, selectedIds, onToggleSelect, onToggleSelectAll, onClearSelection, onBatchDelete }: Props) {
  if (items.length === 0) {
    return (
      <div className="bg-white/[0.03] border border-white/[0.08] rounded-xl md:rounded-2xl py-12 md:py-16 text-center">
        <div className="w-12 h-12 md:w-14 md:h-14 flex items-center justify-center mx-auto mb-3 md:mb-4 rounded-full bg-white/[0.04]">
          <i className="ri-inbox-2-line text-gray-600 text-2xl md:text-3xl"></i>
        </div>
        <p className="text-gray-400 text-sm md:text-base font-medium mb-1">발송 내역이 없습니다</p>
        <p className="text-gray-600 text-xs md:text-sm">조건을 변경하거나 필터를 초기화해 보세요</p>
      </div>
    );
  }

  return (
    <>
      {/* Batch action bar */}
      {selectedIds.size > 0 && (
        <div className="flex items-center justify-between bg-red-500/10 border border-red-500/25 rounded-xl px-4 py-3 mb-3 animate-in fade-in slide-in-from-top-2 duration-200">
          <div className="flex items-center gap-2 md:gap-3">
            <div className="w-7 h-7 md:w-8 md:h-8 flex items-center justify-center rounded-lg bg-red-500/20 shrink-0">
              <i className="ri-delete-bin-line text-red-400 text-sm md:text-base"></i>
            </div>
            <div>
              <p className="text-red-300 text-xs md:text-sm font-semibold">
                {selectedIds.size}건 선택됨
              </p>
              <p className="text-red-400/60 text-[10px] md:text-xs">삭제된 데이터는 복구할 수 없습니다</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={onClearSelection}
              className="flex items-center gap-1 px-3 py-1.5 rounded-lg bg-white/[0.06] text-gray-400 hover:text-white hover:bg-white/[0.1] text-xs font-medium cursor-pointer whitespace-nowrap transition-all"
            >
              <i className="ri-close-line text-sm"></i>
              선택 해제
            </button>
            <button
              onClick={onBatchDelete}
              className="flex items-center gap-1 px-3 py-1.5 md:px-4 md:py-2 rounded-lg bg-red-600 hover:bg-red-500 text-white text-xs font-semibold cursor-pointer whitespace-nowrap transition-all"
            >
              <i className="ri-delete-bin-line text-sm"></i>
              <span className="hidden sm:inline">삭제</span>
            </button>
          </div>
        </div>
      )}

      <div className="bg-white/[0.03] border border-white/[0.08] rounded-xl md:rounded-2xl overflow-hidden">
        {/* Desktop table */}
        <div className="hidden md:block overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-white/[0.07] bg-white/[0.02]">
                <th className="w-10 px-3 py-3.5">
                  <div className="w-4 h-4 flex items-center justify-center">
                    <input
                      type="checkbox"
                      checked={items.length > 0 && selectedIds.size === items.length}
                      onChange={onToggleSelectAll}
                      className="w-4 h-4 rounded border-white/20 bg-white/[0.06] text-red-500 focus:ring-red-500/30 cursor-pointer"
                    />
                  </div>
                </th>
                <th className="text-left text-gray-500 text-xs font-medium px-2 py-3.5 whitespace-nowrap">유형</th>
                <th className="text-left text-gray-500 text-xs font-medium px-4 py-3.5 whitespace-nowrap">제목 / 내용</th>
                <th className="text-left text-gray-500 text-xs font-medium px-4 py-3.5 whitespace-nowrap">수신자</th>
                <th className="text-left text-gray-500 text-xs font-medium px-4 py-3.5 whitespace-nowrap">성공 / 실패</th>
                <th className="text-left text-gray-500 text-xs font-medium px-4 py-3.5 whitespace-nowrap">비용</th>
                <th className="text-left text-gray-500 text-xs font-medium px-4 py-3.5 whitespace-nowrap">상태</th>
                <th className="text-left text-gray-500 text-xs font-medium px-4 py-3.5 whitespace-nowrap">발송 시각</th>
                <th className="text-left text-gray-500 text-xs font-medium px-4 py-3.5 whitespace-nowrap">액션</th>
              </tr>
            </thead>
            <tbody>
              {items.map((item, idx) => {
                const statusCfg = STATUS_CONFIG[item.status];
                const typeCfg = TYPE_CONFIG[item.type];
                const totalCount = item.recipients.length > 0 ? item.recipients.length : (item.totalCount > 0 ? item.totalCount : (item.successCount + item.failCount));

                return (
                  <tr
                    key={item.id}
                    className={`border-b border-white/[0.04] hover:bg-white/[0.03] transition-colors cursor-pointer group ${
                      idx === items.length - 1 ? "border-b-0" : ""
                    }`}
                    onClick={() => onSelect(item)}
                  >
                    <td className="px-3 py-4" onClick={(e) => e.stopPropagation()}>
                      <div className="w-4 h-4 flex items-center justify-center">
                        <input
                          type="checkbox"
                          checked={selectedIds.has(item.id)}
                          onChange={() => onToggleSelect(item.id)}
                          className="w-4 h-4 rounded border-white/20 bg-white/[0.06] text-red-500 focus:ring-red-500/30 cursor-pointer"
                        />
                      </div>
                    </td>
                    <td className="px-2 py-4" onClick={() => onSelect(item)}>
                      <div className="flex flex-col items-start gap-1">
                        <span className={`text-xs font-bold px-2 py-0.5 rounded-md whitespace-nowrap ${typeCfg.className}`}>
                          {item.type}
                        </span>
                        {item.hasImage && (
                          <span className="flex items-center gap-0.5 text-gray-500 text-xs">
                            <i className="ri-image-line text-xs"></i>
                            이미지
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-4">
                      {item.subject && (
                        <p className="text-white text-sm font-medium truncate mb-0.5">{item.subject}</p>
                      )}
                      <p className="text-gray-400 text-xs truncate leading-relaxed">{item.message}</p>
                    </td>
                    <td className="px-4 py-4">
                      <span className="text-gray-300 text-sm font-medium whitespace-nowrap">
                        {totalCount.toLocaleString()}명
                      </span>
                      {totalCount === 1 && item.recipients[0] && (
                        <p className="text-gray-500 text-xs whitespace-nowrap mt-0.5">
                          {item.recipients[0].name ?? item.recipients[0].phone}
                        </p>
                      )}
                      {totalCount > 1 && (() => {
                        const maxBatch = Math.max(...item.recipients.map((r) => r.batchIndex ?? 0));
                        if (maxBatch > 0) {
                          return (
                            <p className="text-violet-400/70 text-xs whitespace-nowrap mt-0.5">
                              <i className="ri-stack-line text-xs mr-0.5"></i>
                              {maxBatch + 1}개 배치
                            </p>
                          );
                        }
                        return null;
                      })()}
                    </td>
                    <td className="px-4 py-4">
                      {item.status === "scheduled" ? (
                        <span className="text-gray-500 text-xs whitespace-nowrap">예약 대기</span>
                      ) : (
                        <div className="space-y-1">
                          <div className="flex items-center gap-2 whitespace-nowrap">
                            <span className="text-emerald-400 text-sm font-semibold">{item.successCount.toLocaleString()}</span>
                            <span className="text-gray-600 text-xs">/</span>
                            <span className={`text-sm font-semibold ${item.failCount > 0 ? "text-red-400" : "text-gray-600"}`}>
                              {item.failCount.toLocaleString()}
                            </span>
                          </div>
                          {(() => {
                            const total = item.successCount + item.failCount;
                            if (total === 0) return null;
                            const rate = Math.round((item.successCount / total) * 100);
                            return (
                              <div className="flex items-center gap-1.5">
                                <div className="h-1 w-12 bg-white/[0.08] rounded-full overflow-hidden">
                                  <div
                                    className={`h-full rounded-full transition-all ${
                                      rate >= 90 ? "bg-emerald-500" : rate >= 70 ? "bg-amber-500" : "bg-red-500"
                                    }`}
                                    style={{ width: `${rate}%` }}
                                  />
                                </div>
                                <span className={`text-[10px] font-bold ${
                                  rate >= 90 ? "text-emerald-400" : rate >= 70 ? "text-amber-400" : "text-red-400"
                                }`}>
                                  {rate}%
                                </span>
                              </div>
                            );
                          })()}
                        </div>
                      )}
                    </td>
                    <td className="px-4 py-4">
                      <div className="flex flex-col">
                        <span className="text-gray-300 text-sm font-medium whitespace-nowrap">
                          ₩{item.cost.toLocaleString()}
                        </span>
                        {item.refundAmount != null && item.refundAmount > 0 && (
                          <span className="text-emerald-400 text-[10px] font-medium whitespace-nowrap mt-0.5">
                            <i className="ri-refund-2-line text-[10px] mr-0.5"></i>
                            +₩{item.refundAmount.toLocaleString()} 환불
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-4">
                      <span className={`inline-flex items-center gap-1.5 text-xs font-medium px-2.5 py-1 rounded-full whitespace-nowrap ${statusCfg.className}`}>
                        <span className={`w-1.5 h-1.5 rounded-full ${statusCfg.dotClass}`}></span>
                        {statusCfg.label}
                      </span>
                    </td>
                    <td className="px-4 py-4">
                      <span className="text-gray-500 text-xs whitespace-nowrap">{item.sentAt}</span>
                    </td>
                    <td className="px-4 py-4" onClick={(e) => e.stopPropagation()}>
                      <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                        <button
                          onClick={() => onSelect(item)}
                          className="w-7 h-7 flex items-center justify-center rounded-lg bg-white/[0.06] text-gray-400 hover:text-white hover:bg-white/[0.1] cursor-pointer transition-all"
                          title="상세 보기"
                        >
                          <i className="ri-eye-line text-sm"></i>
                        </button>
                        {item.status === "pending" && (
                          <button
                            onClick={() => onCancel(item)}
                            disabled={cancellingIds.has(item.id)}
                            className="w-7 h-7 flex items-center justify-center rounded-lg bg-red-500/15 text-red-400 hover:text-red-300 hover:bg-red-500/25 cursor-pointer transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                            title="발송 중지"
                          >
                            <i className={`${cancellingIds.has(item.id) ? "ri-loader-4-line animate-spin" : "ri-stop-circle-line"} text-sm`}></i>
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {/* Mobile card view */}
        <div className="md:hidden divide-y divide-white/[0.04]">
          {items.map((item) => {
            const statusCfg = STATUS_CONFIG[item.status];
            const typeCfg = TYPE_CONFIG[item.type];
            const totalCount = item.recipients.length > 0 ? item.recipients.length : (item.totalCount > 0 ? item.totalCount : (item.successCount + item.failCount));

            return (
              <div
                key={item.id}
                className="px-3 py-3 space-y-2.5 transition-colors active:bg-white/[0.04]"
              >
                {/* Top row: checkbox + type + status */}
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <div className="w-4 h-4 flex items-center justify-center shrink-0" onClick={(e) => e.stopPropagation()}>
                      <input
                        type="checkbox"
                        checked={selectedIds.has(item.id)}
                        onChange={() => onToggleSelect(item.id)}
                        className="w-4 h-4 rounded border-white/20 bg-white/[0.06] text-red-500 focus:ring-red-500/30 cursor-pointer"
                      />
                    </div>
                    <span className={`inline-flex items-center px-2 py-0.5 rounded text-[10px] font-semibold ${typeCfg.className}`} onClick={() => onSelect(item)}>
                      {item.type}
                    </span>
                    {item.hasImage && (
                      <span className="inline-flex items-center gap-0.5 text-amber-400 text-[10px]">
                        <i className="ri-image-line text-[10px]"></i>
                        이미지
                      </span>
                    )}
                  </div>
                  <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium ${statusCfg.className} cursor-pointer`} onClick={() => onSelect(item)}>
                    <span className={`w-1.5 h-1.5 rounded-full ${statusCfg.dotClass}`}></span>
                    {statusCfg.label}
                  </span>
                </div>

                {/* Message preview */}
                <div>
                  {item.subject && (
                    <p className="text-white text-xs font-medium truncate mb-0.5">{item.subject}</p>
                  )}
                  <p className="text-gray-400 text-xs line-clamp-2">{item.message}</p>
                </div>

                {/* Info grid */}
                <div className="grid grid-cols-2 gap-x-3 gap-y-1.5">
                  <div className="flex items-center gap-1.5">
                    <div className="w-3.5 h-3.5 flex items-center justify-center">
                      <i className="ri-calendar-line text-gray-500 text-[10px]"></i>
                    </div>
                    <span className="text-gray-300 text-[10px]">{item.sentAt}</span>
                  </div>
                  <div className="flex items-center gap-1.5">
                    <div className="w-3.5 h-3.5 flex items-center justify-center">
                      <i className="ri-group-line text-gray-500 text-[10px]"></i>
                    </div>
                    <span className="text-gray-300 text-[10px]">{totalCount.toLocaleString()}명</span>
                  </div>
                  <div className="flex items-center gap-1.5">
                    <div className="w-3.5 h-3.5 flex items-center justify-center">
                      <i className="ri-coin-line text-gray-500 text-[10px]"></i>
                    </div>
                    <span className="text-gray-300 text-[10px]">₩{item.cost.toLocaleString()}</span>
                    {item.refundAmount != null && item.refundAmount > 0 && (
                      <span className="text-emerald-400 text-[10px] font-medium">
                        <i className="ri-refund-2-line text-[10px] mr-0.5"></i>
                        +₩{item.refundAmount.toLocaleString()}
                      </span>
                    )}
                  </div>
                  {item.status !== "scheduled" && (
                    <div className="flex items-center gap-1.5">
                      <span className="text-emerald-400 text-[10px] font-medium">{item.successCount.toLocaleString()}</span>
                      <span className="text-gray-600 text-[10px]">/</span>
                      <span className={`text-[10px] font-medium ${item.failCount > 0 ? "text-red-400" : "text-gray-600"}`}>
                        {item.failCount.toLocaleString()}
                      </span>
                      {(() => {
                        const total = item.successCount + item.failCount;
                        if (total === 0) return null;
                        const rate = Math.round((item.successCount / total) * 100);
                        return (
                          <span className={`text-[10px] font-bold ml-0.5 ${
                            rate >= 90 ? "text-emerald-400" : rate >= 70 ? "text-amber-400" : "text-red-400"
                          }`}>
                            · {rate}%
                          </span>
                        );
                      })()}
                    </div>
                  )}
                </div>

                {/* Mobile action buttons */}
                <div className="flex items-center gap-2 pt-1" onClick={(e) => e.stopPropagation()}>
                  <button
                    onClick={() => onSelect(item)}
                    className="flex items-center gap-1 px-2.5 py-1 rounded-lg bg-white/[0.06] text-gray-400 text-[10px] font-medium hover:text-white hover:bg-white/[0.1] cursor-pointer transition-all"
                  >
                    <i className="ri-eye-line text-[10px]"></i>
                    상세
                  </button>
                  {item.status === "pending" && (
                    <button
                      onClick={() => onCancel(item)}
                      disabled={cancellingIds.has(item.id)}
                      className="flex items-center gap-1 px-2.5 py-1 rounded-lg bg-red-500/15 text-red-400 text-[10px] font-medium hover:text-red-300 hover:bg-red-500/25 cursor-pointer transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <i className={`${cancellingIds.has(item.id) ? "ri-loader-4-line animate-spin" : "ri-stop-circle-line"} text-[10px]`}></i>
                      중지
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="border-t border-white/[0.07] px-3 md:px-5 py-3 md:py-3.5 flex items-center justify-between">
            <p className="text-gray-500 text-[10px] md:text-xs">
              {currentPage} / {totalPages} 페이지
            </p>
            <div className="flex items-center gap-0.5 md:gap-1">
              <button
                onClick={() => onPageChange(1)}
                disabled={currentPage === 1}
                className="w-6 h-6 md:w-7 md:h-7 flex items-center justify-center rounded-lg text-gray-500 hover:text-white hover:bg-white/[0.06] disabled:opacity-30 disabled:cursor-not-allowed cursor-pointer transition-all"
              >
                <i className="ri-skip-back-mini-line text-xs md:text-sm"></i>
              </button>
              <button
                onClick={() => onPageChange(currentPage - 1)}
                disabled={currentPage === 1}
                className="w-6 h-6 md:w-7 md:h-7 flex items-center justify-center rounded-lg text-gray-500 hover:text-white hover:bg-white/[0.06] disabled:opacity-30 disabled:cursor-not-allowed cursor-pointer transition-all"
              >
                <i className="ri-arrow-left-s-line text-xs md:text-sm"></i>
              </button>
              {Array.from({ length: Math.min(totalPages, 5) }, (_, i) => {
                let page = i + 1;
                if (totalPages > 5) {
                  const start = Math.max(1, Math.min(currentPage - 2, totalPages - 4));
                  page = start + i;
                }
                return (
                  <button
                    key={page}
                    onClick={() => onPageChange(page)}
                    className={`w-6 h-6 md:w-7 md:h-7 flex items-center justify-center rounded-lg text-[10px] md:text-xs font-medium cursor-pointer transition-all ${
                      currentPage === page
                        ? "bg-gradient-to-r from-violet-600 to-pink-600 text-white"
                        : "text-gray-400 hover:text-white hover:bg-white/[0.06]"
                    }`}
                  >
                    {page}
                  </button>
                );
              })}
              <button
                onClick={() => onPageChange(currentPage + 1)}
                disabled={currentPage === totalPages}
                className="w-6 h-6 md:w-7 md:h-7 flex items-center justify-center rounded-lg text-gray-500 hover:text-white hover:bg-white/[0.06] disabled:opacity-30 disabled:cursor-not-allowed cursor-pointer transition-all"
              >
                <i className="ri-arrow-right-s-line text-xs md:text-sm"></i>
              </button>
              <button
                onClick={() => onPageChange(totalPages)}
                disabled={currentPage === totalPages}
                className="w-6 h-6 md:w-7 md:h-7 flex items-center justify-center rounded-lg text-gray-500 hover:text-white hover:bg-white/[0.06] disabled:opacity-30 disabled:cursor-not-allowed cursor-pointer transition-all"
              >
                <i className="ri-skip-forward-mini-line text-xs md:text-sm"></i>
              </button>
            </div>
          </div>
        )}
      </div>
    </>
  );
}